import { useState } from 'react';
import { Alert, Button, Card, Icon, Table, cn, type Column } from '@evogard/ui';
import type { ManagementDeadlines } from '@evogard/contract';
import {
  CLAIM_DEPARTMENTS,
  claimPhaseLabel,
  count,
  type ClaimFlowPeriod,
} from '@evogard/domain';
import { LINE_LABEL, LINE_TONE } from './managementLabels.js';
import { useManagementDeadlines } from './useManagement.js';

/**
 * O prazo por etapa (26/10/2026): quanto tempo o sinistro fica em cada etapa
 * de trabalho, e quem está nela agora com o SLA expirado.
 *
 * Desde 29/10/2026 (pedido do dono) a barra é o TEMPO MÉDIO na etapa, no
 * desenho do tempo por departamento: a barra cheia é a média de quem já saiu,
 * o traço sobre ela é a mediana, e a barra fina e clara embaixo é há quantos
 * dias estão os que ainda estão lá. Antes ela contava quem estava na etapa,
 * com o SLA em cores; o SLA expirado continua, escrito ao lado do número.
 *
 * As cores são as das linhas do mapa e do fluxo, e cada número está escrito.
 */

const days1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const inDays = (n: number) => `${days1.format(n)} ${n === 1 ? 'dia' : 'dias'}`;

type Stage = ManagementDeadlines['stages'][number];
const nowIn = (s: Stage) => s.withinDeadline + s.expired + s.noDeadline;
const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

export function ClaimDeadlinesChart({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementDeadlines(days);
  const data = query.data;
  const any = !!data?.stages.length;
  const working = data ? data.stages.reduce((n, s) => n + nowIn(s), 0) : 0;
  const expired = data ? data.stages.reduce((n, s) => n + s.expired, 0) : 0;

  return (
    <Card
      as="figure"
      title="Prazo por etapa"
      descricao="Dias em cada etapa de trabalho: média e mediana de quem já saiu, há quanto tempo estão os que ficaram e quantos já passaram do SLA."
      actions={
        any &&
        !query.isError && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar o prazo por etapa">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !data ? (
        <div
          className="h-64 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : !any ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro aberto nos últimos {days} dias passou por uma etapa de
          trabalho. Quando passar, o gráfico mostra quanto tempo ficou em cada uma.
        </p>
      ) : asTable ? (
        <DeadlinesTable data={data} />
      ) : (
        <DeadlinesFigure data={data} />
      )}
      {data && any && (
        <figcaption className="mt-3 text-xs text-texto-suave">
          {count(working, 'sinistro está', 'sinistros estão')} numa etapa de trabalho
          agora;{' '}
          {expired > 0 ? (
            <b className="font-semibold text-erro">
              {count(expired, 'com o SLA expirado', 'com o SLA expirado')} (
              {share(expired, working)}%)
            </b>
          ) : (
            'nenhum com o SLA expirado'
          )}
          .
        </figcaption>
      )}
    </Card>
  );
}

function DeadlinesFigure({ data }: { data: ManagementDeadlines }) {
  const max = Math.max(
    1,
    ...data.stages.flatMap((s) => [
      s.meanDays ?? 0,
      s.medianDays ?? 0,
      s.inProgressMeanDays ?? 0,
    ]),
  );
  const pct = (n: number) => `${(n / max) * 100}%`;
  return (
    <div className="flex flex-col gap-4">
      {CLAIM_DEPARTMENTS.map((department) => {
        const stages = data.stages.filter((s) => s.department === department);
        if (stages.length === 0) return null;
        return (
          <section key={department} className="flex flex-col gap-1.5">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-texto">
              <i
                aria-hidden
                className={cn('size-2.5 rounded-pilula', LINE_TONE[department].fill)}
              />
              {LINE_LABEL[department]}
            </h3>
            <ol
              className="flex flex-col gap-2"
              aria-label={`Tempo médio por etapa — ${LINE_LABEL[department]}`}
            >
              {stages.map((s) => (
                <li
                  key={s.phase}
                  className="grid grid-cols-[minmax(0,11rem)_minmax(0,1fr)_minmax(0,6.5rem)] items-center gap-x-3 gap-y-0.5 text-xs"
                >
                  <span
                    className="truncate text-texto-suave"
                    title={claimPhaseLabel(department, s.phase)}
                  >
                    {claimPhaseLabel(department, s.phase)}
                  </span>
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="relative h-2.5 rounded-pilula bg-superficie-suave">
                      {s.meanDays !== null && (
                        <span
                          className={cn(
                            'absolute inset-y-0 left-0 rounded-pilula',
                            LINE_TONE[department].fill,
                          )}
                          style={{ width: pct(s.meanDays) }}
                        />
                      )}
                      {s.medianDays !== null && (
                        <span
                          aria-hidden
                          title={`Mediana: ${inDays(s.medianDays)}`}
                          className="absolute -inset-y-1 w-0.5 rounded-pilula bg-texto"
                          style={{ left: `calc(${pct(s.medianDays)} - 1px)` }}
                        />
                      )}
                    </span>
                    {s.inProgressMeanDays !== null && (
                      <span className="relative h-1">
                        <span
                          className={cn(
                            'absolute inset-y-0 left-0 rounded-pilula opacity-35',
                            LINE_TONE[department].fill,
                          )}
                          style={{ width: pct(s.inProgressMeanDays) }}
                        />
                      </span>
                    )}
                  </span>
                  <span className="text-right tabular-nums">
                    {s.meanDays === null ? (
                      <span className="text-texto-suave">—</span>
                    ) : (
                      <b className="font-semibold text-texto">{inDays(s.meanDays)}</b>
                    )}
                  </span>
                  <span
                    className="col-start-2 col-end-4 text-texto-suave"
                    /* a linha de apoio: a mediana, quem saiu, quem está e o SLA */
                  >
                    {s.done > 0
                      ? `Mediana de ${inDays(s.medianDays!)} · ${count(s.done, 'já saiu', 'já saíram')}`
                      : 'Nenhum saiu ainda'}
                    {nowIn(s) > 0 && (
                      <>
                        {' · '}
                        {count(nowIn(s), 'está lá', 'estão lá')}
                        {s.inProgressMeanDays !== null &&
                          `, há ${inDays(s.inProgressMeanDays)} em média`}
                      </>
                    )}
                    {s.expired > 0 && (
                      <span className="text-erro"> · {s.expired} com SLA expirado</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        );
      })}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-texto-suave">
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-2.5 w-5 rounded-pilula bg-texto-suave" />
          média de quem já saiu
        </span>
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-3.5 w-0.5 rounded-pilula bg-texto" />
          mediana
        </span>
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-1.5 w-5 rounded-pilula bg-texto-suave opacity-35" />
          há quanto tempo estão os que ainda estão lá
        </span>
      </div>
    </div>
  );
}

function DeadlinesTable({ data }: { data: ManagementDeadlines }) {
  const orDash = (n: number | null) => (n === null ? '—' : inDays(n));
  const columns: Column<Stage>[] = [
    {
      title: 'Etapa',
      cell: (r) =>
        `${LINE_LABEL[r.department]} › ${claimPhaseLabel(r.department, r.phase)}`,
      alignment: 'inicio',
    },
    { title: 'Já saíram', cell: (r) => r.done, alignment: 'fim' },
    { title: 'Média', cell: (r) => orDash(r.meanDays), alignment: 'fim' },
    { title: 'Mediana', cell: (r) => orDash(r.medianDays), alignment: 'fim' },
    { title: 'Na etapa agora', cell: nowIn, alignment: 'fim' },
    { title: 'Há (média)', cell: (r) => orDash(r.inProgressMeanDays), alignment: 'fim' },
    { title: 'SLA expirado', cell: (r) => r.expired, alignment: 'fim' },
    { title: 'Dentro do prazo', cell: (r) => r.withinDeadline, alignment: 'fim' },
    { title: 'Sem SLA', cell: (r) => r.noDeadline, alignment: 'fim' },
  ];
  return (
    <Table
      columns={columns}
      items={data.stages}
      rowKey={(r) => `${r.department}:${r.phase}`}
      label={`Tempo por etapa dos abertos nos últimos ${data.days} dias`}
      empty="Nenhum sinistro passou por uma etapa de trabalho."
    />
  );
}
